import React, { useState, useMemo, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Card,
  CardBody,
  Button,
  Col,
  Container,
  Row,
  CardText,
  UncontrolledTooltip,
  Badge,
} from "reactstrap";
import { Avatar, Rating, Chip } from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import CancelIcon from "@mui/icons-material/Cancel";
import helpers from "utils/helpers";
import { useTranslation } from "context/TranslationProvider";
import constants from "constants";

const ITEMS_PER_PAGE = 3;

const UserProfileDetailsModal = ({ isOpen, toggle, user }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("overview");
  const [currentPage, setCurrentPage] = useState(1);
  const [showAllReviews, setShowAllReviews] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setActiveTab("overview");
      setCurrentPage(1);
      setShowAllReviews(false);
    }
  }, [isOpen]);

  const listings = useMemo(() => {
    let rawListings = [];
    if (Array.isArray(user?.listings)) {
      rawListings = user.listings;
    } else if (Array.isArray(user?.listings?.listings)) {
      rawListings = user.listings.listings;
    }

    return rawListings.filter((listing) => !helpers.isExpired(listing));
  }, [user]);

  const totalPages = Math.ceil(listings.length / ITEMS_PER_PAGE);

  const paginatedListings = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return listings.slice(start, start + ITEMS_PER_PAGE);
  }, [listings, currentPage]);

  if (!user) return null;

  const isUserVerified = user?.isVerified;
  const reviews = user?.reviews || [];
  const reviewsCount = reviews.length;

  const averageRating =
    reviewsCount > 0 && user?.averageRating && !isNaN(user.averageRating)
      ? Number(user.averageRating)
      : 0;

  const profilePicUrl = user?.profilePictureUrl
    ? `${constants.s3Bucket}/profile-pics/${user.profilePictureUrl}`
    : null;

  const sectionTabs = helpers.getUserProfileModalTabs(t);

  const avatarProps = !profilePicUrl 
    ? helpers.stringAvatar(`${user.firstName} ${user.lastName || ""}`)
    : {};

  const avatarSx = {
    width: 120,
    height: 120,
    fontSize: "2.5rem",
    ...(avatarProps.sx || {}),
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl">
      <ModalHeader toggle={toggle} className="border">
        {t("page.user-profile.modal.userDetails.title", "User Profile Details")}
      </ModalHeader>
      
      <ModalBody className="p-0">
        <Container fluid className="p-4">
          <Row>
            <Col lg="4">
              <Card className="card-profile shadow border-0 mb-3">
                <CardBody className="pt-4 text-center">
                  <div className="d-flex justify-content-center mb-3">
                    <Avatar
                      className="uppercase user-profile-img cursor-pointer"
                      src={profilePicUrl || undefined}
                      {...avatarProps}
                      sx={avatarSx}
                    />
                  </div>

                  <h5 className="h3 font-weight-bold mb-1 d-flex justify-content-center align-items-center">
                    {user.firstName} {user.lastName}

                    {isUserVerified ? (
                      <>
                        <VerifiedIcon
                          id={`verify-${user._id || 'user'}`}
                          className="icon-status verifiedIcon"
                        />
                        <UncontrolledTooltip target={`verify-${user._id || 'user'}`}>
                          {t("page.user-profile.tooltip.verifiedUser", "Verified User")}
                        </UncontrolledTooltip>
                      </>
                    ) : (
                      <>
                        <CancelIcon
                          id={`verify-${user._id || 'user'}`}
                          className="icon-status unverifiedIcon"
                        />
                        <UncontrolledTooltip target={`verify-${user._id || 'user'}`}>
                          {t("page.user-profile.tooltip.unverifiedUser", "Unverified User")}
                        </UncontrolledTooltip>
                      </>
                    )}
                  </h5>

                  <div className="d-flex justify-content-center mt-2">
                    {user?.email_verified ? (
                      <Badge color="success" className="px-2 py-1">
                        {t("page.user-profile.header.emailVerified", "Email Verified")}
                      </Badge>
                    ) : (
                      <Badge color="warning" className="px-2 py-1">
                        {t("page.user-profile.header.emailNotVerified", "Email Not Verified")}
                      </Badge>
                    )}
                  </div>

                  <div className="text-center mt-3 text-muted">
                    <div className="h5 font-weight-300">
                      <strong>{t("page.user-profile.modal.joined", "Joined")}:</strong>{" "}
                      {helpers.getFormattedDate(user.createdAt, t)}
                    </div>

                    <div className="h5 font-weight-300">
                      <strong>{t("page.user-profile.form.country", "Country")}:</strong>{" "}
                      {(typeof user?.country === "object" ? user?.country?.label : user?.country) || t("common.na", "N/A")}
                    </div>
                  </div>
                </CardBody>
              </Card>

              <Card className="shadow border-0 mb-3">
                <CardBody>
                  <h4 className="heading-md text-muted mb-3 pb-2 border-bottom">
                    {t("page.user-profile.modal.about", "About")}
                  </h4>

                  <ul className="pl-3 m-0 heading-sm text-muted mb-0">
                    {user?.aboutInsights?.deliveredCount > 0 && (
                      <li className="mb-2">
                        <strong>{user.aboutInsights.deliveredCount}</strong>{" "}
                        {t("page.user-profile.modal.deliveriesCompleted", "successful deliveries completed")}
                      </li>
                    )}

                    {user?.aboutInsights?.experiencedRoutes?.length > 0 && (
                      <li className="mb-2">
                        <strong>{t("page.user-profile.modal.experiencedRoutes", "Experienced courier for routes")}:</strong>
                        <ul className="pl-3 mt-1">
                          {user.aboutInsights.experiencedRoutes.map((route, i) => (
                            <li key={i}>{route}</li>
                          ))}
                        </ul>
                      </li>
                    )}

                    {user?.aboutInsights?.capacity && (
                      <li className="mb-2">
                        <strong>{t("page.user-profile.modal.capacityUpTo", "Capacity up to")}:</strong>{" "}
                        {user.aboutInsights.capacity}
                      </li>
                    )}

                    {!user?.aboutInsights && (
                      <li>{t("page.user-profile.modal.noDescription", "No description available")}</li>
                    )}
                  </ul>
                </CardBody>
              </Card>
            </Col>

            <Col lg="8" className="d-flex flex-column">
              <Card className="shadow border-0 mb-3 flex-grow-1 user-profile-main-card">
                <CardBody>
                  <div className="mb-4">
                    <h2 className="heading-lg font-weight-bold m-0 mb-2">
                      {user.firstName} {user.lastName}
                    </h2>

                    <div className="d-flex align-items-center gap-2">
                      <Rating value={averageRating} readOnly precision={0.5} />
                      <span className="text-sm text-muted ml-2">
                        {averageRating.toFixed(1)} ({reviewsCount}{" "}
                        {t("page.user-profile.modal.reviews", "Reviews")}
                        )
                      </span>
                    </div>
                  </div>

                  <div className="d-flex gap-4 border-bottom mb-4">
                    {sectionTabs.map((tab) => (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => {
                          setActiveTab(tab.key);
                          setCurrentPage(1);
                          setShowAllReviews(false);
                        }}
                        className={`text-capitalize px-3 py-2 border-0 profile-modal-tab-btn ${
                          activeTab === tab.key
                            ? "text-white font-weight-bold bg-primary profile-modal-tab-active"
                            : "text-muted bg-transparent"
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {activeTab === "overview" && (
                    <div>
                      <h4 className="heading-md text-muted font-weight-bold mb-4">
                        {t("page.user-profile.modal.userDetails.quickDetails", "Quick Details")}
                      </h4>

                      <Row>
                        <Col md="6" xl="3">
                          <Card className="card-stats mb-4 mb-xl-0 shadow-none border-0 profile-stat-box">
                            <CardBody className="p-3 text-center">
                              <h5 className="text-uppercase text-muted mb-2 text-xs font-weight-bold">
                                {t("page.user-profile.modal.rating", "Rating")}
                              </h5>
                              <span className="h4 font-weight-bold mb-0">
                                {averageRating.toFixed(1)} / 5
                              </span>
                            </CardBody>
                          </Card>
                        </Col>

                        <Col md="6" xl="3">
                          <Card className="card-stats mb-4 mb-xl-0 shadow-none border-0 profile-stat-box">
                            <CardBody className="p-3 text-center">
                              <h5 className="text-uppercase text-muted mb-2 text-xs font-weight-bold">
                                {t("page.user-profile.modal.onTime", "On-time")}
                              </h5>
                              <span className="h4 font-weight-bold mb-0">
                                {user?.onTimePercentage ?? 0}%
                              </span>
                            </CardBody>
                          </Card>
                        </Col>

                        <Col md="6" xl="3">
                          <Card className="card-stats mb-4 mb-xl-0 shadow-none border-0 profile-stat-box">
                            <CardBody className="p-3 text-center">
                              <h5 className="text-uppercase text-muted mb-2 text-xs font-weight-bold">
                                {t("page.user-profile.header.deliveries", "Deliveries")}
                              </h5>
                              <span className="h4 font-weight-bold mb-0">
                                {user?.listings?.deliveredCount ?? 0}
                              </span>
                            </CardBody>
                          </Card>
                        </Col>

                        <Col md="6" xl="3">
                          <Card className="card-stats mb-4 mb-xl-0 shadow-none border-0 profile-stat-box">
                            <CardBody className="p-3 text-center">
                              <h5 className="text-uppercase text-muted mb-2 text-xs font-weight-bold">
                                {t("page.user-profile.header.senderListings", "Sender Listings")}
                              </h5>
                              <span className="h4 font-weight-bold mb-0">
                                {user?.listings?.senderCount ?? 0}
                              </span>
                            </CardBody>
                          </Card>
                        </Col>
                      </Row>
                    </div>
                  )}

                  {activeTab === "reviews" && (
                    <div className="w-100">
                      {!reviews.length ? (
                        <p className="text-muted">{t("page.user-profile.sections.noReviews", "No reviews yet.")}</p>
                      ) : (
                        <div className="d-flex flex-column">
                          <div 
                            className={`d-flex flex-column gap-3 review-container ${
                              showAllReviews ? "show-scroll" : ""
                            }`}
                          >
                            {(showAllReviews ? reviews : reviews.slice(0, 2)).map((review) => {
                              const revPicUrl = review.reviewer_id?.profilePictureUrl
                                ? `${constants.s3Bucket}/profile-pics/${review.reviewer_id.profilePictureUrl}`
                                : "";

                              const currentStatus = helpers.normalizeOrderStatus(
                                review.order_id?.status
                              );
                              const statusClass = helpers.getStatusClass(
                                currentStatus,
                                "order"
                              );

                              return (
                                <Card key={review._id} className="shadow-sm border-0 my-2 bg-secondary">
                                  <CardBody>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <div className="d-flex align-items-center gap-3">
                                        <Avatar
                                          src={revPicUrl}
                                          alt={review.reviewer_id?.firstName}
                                          sx={{ width: 40, height: 40 }}
                                        />
                                        <div className="ml-2">
                                          <h5 className="mb-0 text-sm font-weight-bold">
                                            {review.reviewer_id?.firstName}{" "}
                                            {review.reviewer_id?.lastName}
                                          </h5>
                                          <small className="text-muted">
                                            {new Date(review.createdAt).toLocaleDateString()}
                                          </small>
                                        </div>
                                      </div>
                                      <div className="d-flex align-items-center flex-wrap justify-content-end">
                                        {review.order_id && (
                                          <Chip
                                            label={review.order_id.orderid}
                                            className="mr-2 mb-1 font-weight-bold bg-transparent"
                                            size="small"
                                          />
                                        )}
                                        {review.order_id && (
                                           <Chip
                                             label={t(`page.orders.status.${currentStatus}`, helpers.ORDER_STATUS_API_MAP[currentStatus] || currentStatus)}
                                            size="small"
                                            variant="outlined"
                                            className={`mb-1 font-medium ${statusClass}`}
                                          />
                                        )}
                                      </div>
                                    </div>
                                    <Rating
                                      value={review.rating}
                                      readOnly
                                      size="small"
                                      className="mb-2 mt-2"
                                    />
                                    <CardText className="text-muted text-sm m-0">
                                      {review.message || "—"}
                                    </CardText>
                                  </CardBody>
                                </Card>
                              );
                            })}
                          </div>
                          
                          {reviews.length > 2 && !showAllReviews && (
                            <div className="text-center mt-3">
                              <Button 
                                color="primary" 
                                outline 
                                size="sm" 
                                onClick={() => setShowAllReviews(true)}
                                className="px-4"
                              >
                                {t("page.user-profile.sections.loadMore", "Load More")}
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === "listings" && (
                    <div className="w-100 listings-container">
                      {listings.length === 0 && (
                        <p className="text-muted">
                          {t("page.allListings.noItems", "No items available.")}
                        </p>
                      )}

                      {paginatedListings.map((listing) => {
                        const isSender =
                          listing.type === helpers.CONVERSATION_ROLES?.SENDER || listing.type === "sender";

                        const statusStyles = {
                          pending: "badge-warning",
                          approved: "badge-success",
                          rejected: "badge-danger",
                          closed: "badge-default",
                        };

                        const statusClass =
                          statusStyles[listing.status] || "badge-light text-dark";

                        return (
                          <Card key={listing._id} className="shadow-sm border-0 my-2 bg-secondary">
                            <CardBody className="p-3">
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <span
                                  className={`badge ${
                                    isSender
                                      ? "badge-info"
                                      : "badge-success"
                                  }`}
                                >
                                  {isSender
                                    ? t("page.allListings.sender", "Sender")
                                    : t("page.allListings.traveler", "Traveler")}
                                </span>

                                <span
                                  className={`badge ${statusClass}`}
                                >
                                  {t(`page.userListings.status.${listing.status}`) || listing.status}
                                </span>
                              </div>

                              <div className="d-flex justify-content-between align-items-center">
                                <div className="text-sm">
                                  <span className="font-weight-bold">
                                    {listing?.fromPlace?.name || "-"}
                                  </span>
                                  {" → "}
                                  <span className="font-weight-bold">
                                    {listing?.toPlace?.name || "-"}
                                  </span>
                                </div>

                                <div className="text-xs text-muted font-weight-bold">
                                  {helpers.getFormattedDate(listing.date, t)}
                                </div>
                              </div>
                            </CardBody>
                          </Card>
                        );
                      })}

                      {totalPages > 1 && (
                        <div className="d-flex justify-content-end align-items-center gap-4 mt-4 pt-4 border-top">
                          <Button
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage((p) => p - 1)}
                            outline
                            color="primary"
                            size="sm"
                            className="px-3"
                          >
                            <i className="fa fa-chevron-left mr-1" /> {t("common.back", "Back")}
                          </Button>

                          <span className="text-sm font-weight-bold text-gray border px-3 py-1 rounded-pill">
                            {currentPage} / {totalPages}
                          </span>

                          <Button
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage((p) => p + 1)}
                            outline
                            color="primary"
                            size="sm"
                            className="px-3 ml-2"
                          >
                            {t("page.create-listing.buttons.next", "Next")} <i className="fa fa-chevron-right ml-1" />
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </ModalBody>
    </Modal>
  );
};

export default UserProfileDetailsModal;
